import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
	SectionHeadline,
	LoadingSpinner,
	ReactSwitch,
	ModalBoxSectionEditProfile,
	Button,
	FontAwesome,
	Collapsible,
	CommunityPartnerPartnerCard,
	NoData
} from 'Elements';
import styles from 'partials/homepage/section/staff-section.module.scss';
import { useModal } from 'src/hooks/useModal';
import { Colors } from 'Types';
import { useEffect, useState } from 'react';
import { useCommunityPartnerPartners } from 'src/hooks/useCommunityPartner';

type Props = {
	isLoadingProfile: boolean;
	isFetchingProfile: boolean;
	onChangeValue: (
		objKey: string,
		key: string,
		value: string | boolean
	) => void;
	partners: {
		enabled: boolean;
		headline: string;
		description: string;
	};
};

export const CommunityPartnerPartnerSection = ({
	isLoadingProfile,
	onChangeValue,
	partners
}: Props) => {
	const { t } = useTranslation();
	const [openModal, toggleModal] = useModal();

	const perRow = 8;
	const [next, setNext] = useState(perRow);
	const [loading, setLoading] = useState(false);
	const { data, isLoading } = useCommunityPartnerPartners({
		pageSize: 50,
		pageNumber: 1,
		keyword: '',
		status: '',
		registrationStatus: '',
		publishStatus: 1
	});

	const handleMore = () => {
		setLoading(true);
		setNext(next + perRow);
	};

	useEffect(() => {
		setLoading(false);
	}, [next]);

	return (
		<section className="position-relative mb-5">
			<Container>
				{isLoadingProfile && (
					<div className="d-flex align-items-center justify-content-center">
						<LoadingSpinner />
					</div>
				)}
				{!isLoadingProfile && !isLoading && (
					<Collapsible
						header={
							<div
								className={`d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 ${styles.switcherBox}`}>
								<div className="d-flex flex-wrap align-items-center gap-3">
									<h5 className="fs-5 fw-700 d-flex align-items-center">
										{' '}
										{t('menu.partners')}
									</h5>
									<ReactSwitch
										size="md"
										checked={partners?.enabled}
										onChange={(val: boolean) => {
											onChangeValue(
												'partners',
												'enabled',
												val
											);
										}}
									/>
									<span className="d-flex align-items-center mb-0 py-0 cursor-pointer">
										{t('forms.show-partners-in-home')}
									</span>
								</div>
								<div>
									<Button
										size="sm"
										color={Colors['white-gray']}
										onClick={() => toggleModal()}>
										<FontAwesome
											icon="circle-info"
											size="1x"
											className="text-gray me-1"
										/>
										{t('button.more')}
									</Button>
								</div>
							</div>
						}>
						{!loading && (
							<>
								<SectionHeadline
									title={partners?.headline}
									description={partners?.description}
									titlePlaceholder={`[${t(
										'placeholder.partner-title'
									)}]`}
									descriptionPlaceholder={`[${t(
										'placeholder.partner-description'
									)}]`}
									titleColor="text-gray-5"
									className="mx-auto text-center"
									onUpdateTitle={(val) =>
										onChangeValue(
											'partners',
											'headline',
											val
										)
									}
									onUpdateDescription={(val) =>
										onChangeValue(
											'partners',
											'description',
											val
										)
									}
								/>
								{data && data?.totalItems > 0 && (
									<Row className="justify-content-center">
										{data?.items.map((partner) => (
											<CommunityPartnerPartnerCard
												key={partner.id}
												partner={partner}
											/>
										))}
									</Row>
								)}
								{data?.items && data?.items?.length > next && (
									<div className="d-flex align-items-center justify-content-center mt-3 gap-3">
										<Button
											size="lg"
											color={Colors['border-gray-4']}
											label={
												t(
													'button.more-partners'
												) as string
											}
											className="rounded-pill"
											onClick={handleMore}
										/>
									</div>
								)}
								{data && data?.items?.length === 0 && (
									<NoData
										message={t('title.no-partner-yet')}
									/>
								)}
							</>
						)}
					</Collapsible>
				)}
			</Container>
			{!isLoadingProfile && !partners?.enabled && (
				<div className="overlay overlay_white overlay-box" />
			)}
			<ModalBoxSectionEditProfile
				isOpen={openModal}
				onClose={toggleModal}
				title="info.partners-title"
				description="info.partners-description"
			/>
		</section>
	);
};
